import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { DishType } from '../../types';

export type CartStateType = {
  total: number;
  delivery: number;
  discount: number;
  subtotal: number;
  promoCode: string;
  list: DishType[];
  discountAmount: number;
};

const initialState: CartStateType = {
  total: 0,
  list: [],
  delivery: 0,
  discount: 0,
  subtotal: 0,
  promoCode: '',
  discountAmount: 0,
};

type StateType = typeof initialState;

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (
      state: StateType = initialState,
      action: PayloadAction<DishType>,
    ) => {
      // Normalize isFullPrice: undefined/null/true = full, false = half
      const payloadIsFullPrice = action.payload.isFullPrice !== false;
      
      // Find item with same ID AND same portion type (full/half)
      const inCart = state.list.find(
        (item) => {
          const itemIsFullPrice = item.isFullPrice !== false;
          return item.id === action.payload.id && itemIsFullPrice === payloadIsFullPrice;
        }
      );
      
      const itemPrice = payloadIsFullPrice ? action.payload.price : (action.payload.halfPrice || 0);
      
      if (inCart) {
        // Item with same ID and same portion type exists - increment quantity
        const itemIndex = state.list.findIndex((item: DishType) => {
          const itemIsFullPrice = item.isFullPrice !== false;
          return item.id === action.payload.id && itemIsFullPrice === payloadIsFullPrice;
        });
        
        if (itemIndex !== -1) {
          const item = state.list[itemIndex];
          if (item && item.quantity) {
            item.quantity += 1;
          }
        }
        
        state.subtotal += Number(itemPrice);
        state.discountAmount = Number(
          (state.subtotal - state.total).toFixed(2),
        );
        state.total +=
          Number(itemPrice) * (1 - state.discount / 100);
      } else {
        // Item doesn't exist with same ID and portion type - add as new item
        state.list.push({
          ...action.payload,
          isFullPrice: payloadIsFullPrice, // Ensure isFullPrice is explicitly set
          quantity: 1,
        });
        state.subtotal += Number(itemPrice);
        state.total +=
          Number(itemPrice) * (1 - state.discount / 100);
      }
    },
    removeFromCart: (state, action: PayloadAction<DishType>) => {
      // Normalize isFullPrice: undefined/null/true = full, false = half
      const payloadIsFullPrice = action.payload.isFullPrice !== false;
      
      // Find item with same ID AND same portion type (full/half)
      const inCart = state.list.find(
        (item) => {
          const itemIsFullPrice = item.isFullPrice !== false;
          return item.id === action.payload.id && itemIsFullPrice === payloadIsFullPrice;
        }
      );
      const itemPrice = payloadIsFullPrice ? action.payload.price : (action.payload.halfPrice || 0);
      if (inCart) {
        const itemIndex = state.list.findIndex((item) => {
          const itemIsFullPrice = item.isFullPrice !== false;
          return item.id === action.payload.id && itemIsFullPrice === payloadIsFullPrice;
        });
        
        if (itemIndex !== -1) {
          const item = state.list[itemIndex];
          if (item) {
            if (item.quantity && (item.quantity as number) > 1) {
              item.quantity -= 1;
            } else if (item.quantity === 1) {
              state.list.splice(itemIndex, 1);
            }
          }
        }
        state.subtotal -= Number(itemPrice);
        state.discountAmount = Number(
          (state.subtotal - state.total).toFixed(2),
        );
        state.total -=
          Number(itemPrice) * (1 - state.discount / 100);

        if (state.list.length === 0) {
          state.discount = 0;
          state.promoCode = '';
        }
      }
    },
    setDiscount: (state, action: PayloadAction<number>) => {
      if (state.list.length === 0) {
        state.discount = 0;
      } else {
        state.discount = action.payload;
      }
      const newTotal = state.subtotal * (1 - state.discount / 100);
      state.discountAmount = Number((state.subtotal - newTotal).toFixed(2));
      state.total = state.subtotal * (1 - state.discount / 100);
    },
    resetCart: (state) => {
      state.list = [];
      state.subtotal = 0;
      state.total = 0;
      state.discount = 0;
      state.promoCode = '';
      state.delivery = 0;
      state.discountAmount = 0;
    },
    setPromoCode: (state, action: PayloadAction<string>) => {
      state.promoCode = action.payload;
    },
  },
});

export const {addToCart, resetCart, setDiscount, setPromoCode, removeFromCart} =
  cartSlice.actions;

export default cartSlice.reducer;
